import { useState, useEffect, useCallback } from 'react';
import useImageValidation from './useImageValidation';
import { useCameraCapture } from './useCameraCapture';

/**
 * Хук для управления загрузкой и валидацией изображений
 * Объединяет логику работы с камерой, файлами и валидацией
 */
export const useImageUpload = () => {
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { capturePhoto, isCapturing, error: cameraError, setError: setCameraError } = useCameraCapture();

  // Очистка preview URL при размонтировании или изменении файла
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Синхронизация ошибок камеры
  useEffect(() => {
    if (cameraError) {
      setError(cameraError);
    }
  }, [cameraError]);

  /**
   * Обработка выбранного файла с валидацией
   */
  const handleFileSelect = useCallback(async (file) => {
    if (!file) {
      setImageFile(null);
      setPreviewUrl(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Валидация изображения
      const validation = await useImageValidation(file);

      if (!validation.isValid) {
        setError(validation.error);
        setImageFile(null);
        setPreviewUrl(null);
        setIsLoading(false);
        return;
      }

      // Успешная валидация
      setImageFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setError(null);
    } catch (err) {
      setError('Ошибка при обработке изображения');
      setImageFile(null);
      setPreviewUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Захват фото с камеры
   */
  const handleCameraCapture = useCallback(async () => {
    setError(null);
    setCameraError(null);

    const file = await capturePhoto();

    if (file) {
      await handleFileSelect(file);
    }
  }, [capturePhoto, handleFileSelect, setCameraError]);

  /**
   * Загрузка из галереи
   */
  const handleGallerySelect = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        await handleFileSelect(file);
      }
    };
    input.click();
  }, [handleFileSelect]);

  /**
   * Удаление изображения
   */
  const handleRemoveImage = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageFile(null);
    setPreviewUrl(null);
    setError(null);
  }, [previewUrl]);

  return {
    imageFile,
    previewUrl,
    error,
    isLoading: isLoading || isCapturing,
    handleFileSelect,
    handleCameraCapture,
    handleGallerySelect,
    handleRemoveImage,
    setError
  };
};

export default useImageUpload;

